import React, { useCallback, useMemo } from 'react';
import { KeyboardArrowLeft } from '@mui/icons-material';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import {
  FilterOptionsState,
  IconButton,
  TablePagination,
  TablePaginationProps,
} from '@mui/material';
import { Box } from '@mui/system';
import { StyledAutocomplete, StyledTextField } from 'components/Inputs';
import { useTranslation } from 'react-i18next';

interface Props
  extends Omit<
    TablePaginationProps<'div', { component: 'div' }>,
    'component' | 'ActionsComponent'
  > {
  compactPagePicker?: boolean;
}

interface TablePaginationActionsProps {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement>,
    newPage: number,
  ) => void;
  compact?: boolean;
}

export const CustomTablePagination = (props: Props) => {
  const { t } = useTranslation(['datagrid']);
  const { compactPagePicker, ...rest } = props;

  const ActionsComponent = useMemo(() => {
    if (!compactPagePicker) return TablePaginationActions;
    return (actionProps: TablePaginationActionsProps) => (
      <TablePaginationActions {...actionProps} compact />
    );
  }, [compactPagePicker]);

  return (
    <TablePagination
      labelRowsPerPage={t('entriesPerPage', { ns: 'datagrid' })}
      labelDisplayedRows={({ from, to, count }) =>
        `${from} - ${to} ${t('of', { ns: 'datagrid' })} ${count}`
      }
      {...rest}
      component='div'
      ActionsComponent={ActionsComponent}
    />
  );
};

const TablePaginationActions = (props: TablePaginationActionsProps) => {
  const { count, page, rowsPerPage, onPageChange, compact } = props;

  const handleBackButtonClick = (event: React.MouseEvent<HTMLButtonElement>) =>
    onPageChange(event, page - 1);

  const handleNextButtonClick = (event: React.MouseEvent<HTMLButtonElement>) =>
    onPageChange(event, page + 1);

  const handlePageSelect = (
    event: React.SyntheticEvent<Element, Event>,
    value: unknown,
  ) =>
    onPageChange(event as React.MouseEvent<HTMLButtonElement>, value as number);

  const numberOfPages = useMemo(
    () => Math.ceil(count / rowsPerPage),
    [count, rowsPerPage],
  );

  const handleFilterOptions = useCallback(
    (options: unknown[], state: FilterOptionsState<unknown>) => {
      const numberInput = Number(state.inputValue);
      if (isNaN(numberInput)) return options;
      if (numberInput < 0) return [0];
      if (numberInput > numberOfPages) {
        return [options[options.length - 1]];
      }
      return options.filter((o) => {
        if (!state.inputValue) return true;
        return o === numberInput - 1;
      });
    },
    [numberOfPages],
  );

  const pagesArray = useMemo(
    () => Array.from(Array(numberOfPages).keys()),
    [count, rowsPerPage],
  );

  return (
    <Box
      sx={{
        flexShrink: 0,
        ml: 2.5,
        my: compact ? 0 : 1,
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label='previous page'
      >
        <KeyboardArrowLeft />
      </IconButton>

      <StyledAutocomplete
        padding='dense'
        autoHighlight
        id='combo-box-demo'
        value={pagesArray[page]}
        options={pagesArray}
        filterOptions={handleFilterOptions}
        onChange={handlePageSelect}
        disableClearable
        getOptionLabel={(option) => String((option as number) + 1)}
        renderInput={(params) => (
          <StyledTextField
            width={compact ? 55 : 85}
            padding='dense'
            {...params}
          />
        )}
        {...(compact &&
          ({
            componentsProps: {
              paper: {
                sx: {
                  '& .MuiAutocomplete-option': {
                    fontSize: '0.7rem',
                    minHeight: 'auto',
                    padding: '4px 8px',
                  },
                },
              },
            },
          } as any))}
      />

      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= numberOfPages - 1}
        aria-label='next page'
      >
        <KeyboardArrowRight />
      </IconButton>
    </Box>
  );
};
